import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { StatusCodes } from 'http-status-codes';
import { toast } from 'react-toastify';
import GhostButton from '../GhostButton/GhostButton';
import StyledTableBodyRow from './TableBodyRow.styles';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download.svg';
import type { OrderType } from '../../types/orderTypes';
import { checkIsSuccessfulErrorMessage } from '../../utils/errorCheckHelper';
import { orderAPI } from '../../api/services/orderApi';
import { formattingDate } from '../../utils/formattingDateHelper';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setFileMetadata, setUploadFile } from '../../store/order/orderSlice';
import { getUploadFileName } from '../../utils/getUploadFileName';
import { SkipTraceFileTypeENUM } from '../../utils/skipTraceFileHeaders';
import config from '../../config';

type PropsType = {
  item: OrderType;
  className?: string;
  setIsOpenBillModal?: (isOpen: boolean) => void;
  setIsOpenErrorBalanceModal?: (isOpen: boolean) => void;
};

const TableBodyRow: React.FC<PropsType> = (props) => {
  const { item, className = '' } = props;
  const dispatch = useAppDispatch();
  const isSuccessStatus = checkIsSuccessfulErrorMessage(item.status);
  const currentStatusClassName = (status: string) => `${status.split(' ').join('-').toLowerCase()}-status`;
  const [isUploadingDnc, setIsUploadingDnc] = useState(false);
  const correctDisplayInfo = useCallback((info: number) => {
    return isSuccessStatus ? info : '-';
  }, [isSuccessStatus]);
  const userInfo = useAppSelector((state) => state.userSlice.user);
  const currentDiscount = userInfo.discount ? userInfo.discount : 0;

  async function onClickLink(fileName: string, e: React.MouseEvent<HTMLDivElement>): Promise<void> {
    e.preventDefault();
    try {
      if (fileName) {
        const res = await orderAPI.downloadOutputFile(fileName);
        if (res.status === StatusCodes.OK) {
          const link = document.createElement('a');
          link.href = res.data.data.file;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        toast.error('Skip Ready file is not available yet.');
      }
    } catch (error) {
      toast.error(error.response.data.message);
    }
  }

  const processFile = useCallback(async (data: {
    fileName: string;
    csvBase64: string;
    fields: string[];
    records: number;
  }) => {
    try {
      const { fileName, records, fields, csvBase64 } = data;
      const blacklistColumns = ['phone_1_number', 'phone_2_number', 'phone_3_number', 'phone_4_number', 'phone_5_number'];
      const missingColumns = blacklistColumns.filter((column) => !fields.includes(column));
      if (missingColumns.length > 0) {
        toast.error(`File should contain the following columns: ${missingColumns.join(', ')}`);
        return;
      }
      if (records <= 1) {
        toast.error('File should not be empty.');
        return;
      }
      let cost = 0;
      if (!userInfo.isBlacklistFree) {
        cost = (records - 1) * config.blacklistPricePerRecord * (1 - currentDiscount);
      }
      if (userInfo.balance < cost) {
        props?.setIsOpenErrorBalanceModal?.(true);
        return;
      }
      dispatch(
        setUploadFile({
          name: getUploadFileName(fileName),
          csvBase64,
          records,
          fields,
          skipTraceType: SkipTraceFileTypeENUM.DNC,
        }),
      );
      dispatch(setFileMetadata({
        blacklist_columns: blacklistColumns,
        dnc: true,
        sourceUserFileId: item.userFileId,
      }));
      props?.setIsOpenBillModal?.(true);
    } catch (error) {
      toast.error(error);
    }
  }, [
    item.userFileId,
    dispatch,
    props,
    currentDiscount,
    userInfo.isBlacklistFree,
    userInfo.balance,
  ]);

  const getHitRate = useCallback((successfullRecords: number, totalRecords: number) => {
    if (totalRecords === 0) {
      return 0;
    }
    return ((successfullRecords / totalRecords) * 100).toFixed(2);
  }, []);

  const onClickUploadDnc = useCallback(async (userFileId: number): Promise<void> => {
    try {
      setIsUploadingDnc(true);
      const res = await orderAPI.getFileContent(userFileId);
      if (res.status === StatusCodes.OK) {
        await processFile({
          fileName: res.data.data.fileName,
          csvBase64: res.data.data.csvBase64,
          fields: res.data.data.fields,
          records: res.data.data.records,
        });
      }
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setIsUploadingDnc(false);
    }
  }, [processFile]);

  const orderValueArray = useMemo(() => {
    return [
      // { content: item.userFileId },
      {
        content: (
          <div>
            <GhostButton
              disabled={!isSuccessStatus}
            >
              <div
                onClick={(e) => onClickLink(item?.nameFileAdmin || '', e)}
              >
                <DownloadIcon />
                <p>Cold Call Ready</p>
              </div>
            </GhostButton>
            <GhostButton
              disabled={!isSuccessStatus}
            >
              <div
                onClick={(e) => onClickLink(item?.nameFileAdmin?.replace('O.csv', 'CO.csv') ?? '', e)}
              >
                <DownloadIcon />
                <p>Full Export</p>
              </div>
            </GhostButton>
          </div>
        ),
      },

      {
        content: item.name,
        type: 'p',
      },

      { content: item.type },

      {
        content: item.totalRecords,
      },

      {
        content: correctDisplayInfo(item.successfullRecords),
      },

      {
        content: `${getHitRate(item.successfullRecords, item.totalRecords)}%`,
      },
      {
        content: item.campaignDuplicates,
      },

      { content: item.metadata.email ? 'Yes' : 'No' },

      { content: item.metadata.blacklist ? 'Yes' : 'No' },

      { content: item.metadata.dnc ? 'Yes' : 'No' },

      // {
      //   content: correctDisplayInfo(item.failedRecords),
      // },

      {
        content: item.status,
        type: 'div',
        className: clsx(['order-status', currentStatusClassName(item.status)]),
      },

      { content: `$${item.price.toFixed(2)}` },

      // { content: item.notes || '-' },

      { content: formattingDate(item.createdAt) },
      {
        content: (
          <GhostButton
            disabled={item.type !== 'Owner Skip Trace' || !isSuccessStatus || item.metadata.isUploadedDnc || isUploadingDnc}
          >
            <div
              onClick={() => onClickUploadDnc(item.userFileId)}
            >
              {isUploadingDnc ? 'Uploading...' : 'Upload DNC'}
            </div>
          </GhostButton>
        ),
      },
    ];
  }, [correctDisplayInfo,
    isSuccessStatus,
    item.createdAt,
    // item.failedRecords,
    item.name,
    item?.nameFileAdmin,
    // item.notes,
    item.price,
    item.status,
    item.successfullRecords,
    item.totalRecords,
    item.userFileId,
    item.metadata,
    item.type,
    getHitRate,
    item.campaignDuplicates,
    onClickUploadDnc,
    isUploadingDnc,
  ]);

  return (
    <StyledTableBodyRow>
      {orderValueArray.map((item, index) => {
        if (item.type === 'p') {
          return (
            <td key={index}>
              <p className="styled-table-row__text">{item.content}</p>
            </td>
          );
        }
        if (item.type === 'div') {
          return (
            <td key={index}>
              <div className={clsx(item.className)}>{item.content}</div>
            </td>
          );
        }
        return (
          <td key={index}>
            <div>
              {item.content}
            </div>
          </td>
        );
      })}
    </StyledTableBodyRow>
  );
};

export default TableBodyRow;
